
import { defineComponent, PropType } from '@vue/composition-api';
import { mapGetters, mapActions } from 'vuex';
import { MyBet, MyBetOdd, MyBetResult } from '../../../../../types/sport';
import BetStatusIcon from './BetStatusIcon.vue';
import CashoutDialog from './CashoutDialog.vue';
import { removeParentheses } from '@/composables/utils';

export default defineComponent({
  name: 'BetDetailModal',
  components: {
    BetStatusIcon,
    CashoutDialog
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    bet: {
      type: Object as PropType<MyBet | null>,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    cashout: {
      type: Number,
      default: 0
    },
    cashoutPrematch: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      showCashoutDialog: false,
      cashoutLoading: false
    };
  },
  computed: {
    ...mapGetters({
      getShortCode: 'settings/getShortCode',
      getPlatformSettings: 'settings/getPlatformSettings',
      getOddsType: 'odds/getOddsType',
      getDetailMyBets: 'user/getDetailMyBets',
      getType: 'user/getType',
      getId: 'user/getId'
    }),
    isBetCancelled(): boolean {
      return this.bet?.result === 'cancelled';
    },
    detailOdds(): MyBetOdd[] {
      const detail = this.getDetailMyBets;
      if (detail && detail.odds && Array.isArray(detail.odds)) {
        return detail.odds;
      }
      return this.bet?.odds || [];
    },
    cashoutDisplayValue(): string {
      if (!this.bet) return '';
      const value = this.cashout > 0 ? this.cashout : this.cashoutPrematch;
      return `${this.bet.currencySymbol} ${this.formatAmount(value)}`;
    },
    potentialWinDisplay(): string {
      if (!this.bet) return '';
      return this.getWinning(this.bet);
    }
  },
  methods: {
    ...mapActions({
      resetCart: 'cart/resetCart',
      setOddsInCart: 'cart/setOddsInCart',
      setCreatedAtCart: 'cart/setCreatedAtCart',
      refreshCart: 'cart/refreshCart',
      sentCashout: 'odds/sentCashout',
      fetchMyBets: 'user/fetchMyBets'
    }),
    t(key: string): string {
      return this.$t(key) as string;
    },
    removeParentheses,
    formatDate(date: string | number): string {
      return new Date(date).toLocaleString();
    },
    formatOddDate(time: number): string {
      const date = new Date(time);
      return `${date.toLocaleDateString('en-GB', {
        month: 'numeric',
        day: 'numeric'
      })} ${date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      })}`;
    },
    formatAmount(amount: number | string): string {
      return parseFloat(String(amount)).toFixed(2);
    },
    formatTimeActual(time: number | string): string {
      return parseFloat(String(time)).toFixed(0);
    },
    getWinning(bet: MyBet): string {
      switch (bet.type) {
        case 'single':
        case 'multiple':
          return `${bet.currencySymbol} ${this.formatAmount(
            bet._multiple_detail.winning_total
          )}`;
        case 'integral':
          return `${bet.currencySymbol} ${this.formatAmount(
            bet._integral_detail.winning_total_max
          )}`;
        case 'system':
          return `${bet.currencySymbol} ${this.formatAmount(
            bet._system_detail.winning_all_total
          )}`;
        default:
          return `${bet.currencySymbol} 0.00`;
      }
    },

    getSpecifiers(spec: string): string {
      if (!spec || !spec.includes('#')) return '';
      const split = spec.split('#');
      if (split[1]?.includes(':')) {
        return ` (${split[1]})`;
      } else if (split[1]?.includes('(') && split[1]?.includes('.')) {
        return ` [${split[1]}]`;
      } else if (split[1]) {
        return ` ${split[1]}`;
      }
      return split.length > 2 ? ` ${split[2]}` : '';
    },
    getOddStatus(odd: MyBetOdd): MyBetResult {
      if (this.bet?.cashout && !odd.result) return 'void';
      if (this.bet?.cashout && odd.result) return 'win';
      if (odd.result === undefined) return 'pending';
      if (odd.void) return 'void';
      if (odd.cancelled) return 'cancelled';
      if (odd.result) return 'win';
      return 'lose';
    },
    getResultClass(result: MyBetResult): string {
      switch (result) {
        case 'win':
          return 'text-green-500';
        case 'lose':
          return 'text-red-500';
        case 'void':
          return 'text-blue-500';
        case 'cancelled':
          return 'text-yellow-500';
        default:
          return 'text-yellow-500';
      }
    },
    async handleRebet(): Promise<void> {
      if (!this.bet) return;
      this.$emit('close');
      const now = new Date();
      await this.resetCart();
      await this.setOddsInCart(this.bet.odds);
      await this.setCreatedAtCart(now.getTime());
      await this.refreshCart();
      this.$root.$emit('sectionCart');
    },
    handlePrint(): void {
      if (!this.bet || this.isBetCancelled || this.bet.status === 'review')
        return;

      const code = this.getShortCode ? this.bet.betCode : this.bet.code;
      let printUrl = `${this.getPlatformSettings.print_endpoint}${code}&type_odd=${this.getOddsType}`;

      if (this.getShortCode) {
        printUrl += `&login=${this.getType}&ancestors=${this.getId}`;
      }

      window.open(printUrl, '_blank', 'height=600,width=350');
    },
    handleCashoutClick(): void {
      this.showCashoutDialog = true;
    },
    async handleCashoutConfirm(): Promise<void> {
      if (!this.bet) return;

      this.cashoutLoading = true;
      try {
        const cashoutValue =
          this.cashout > 0
            ? parseFloat(String(this.cashout)).toFixed(2)
            : this.cashoutPrematch > 0
            ? parseFloat(String(this.cashoutPrematch)).toFixed(2)
            : '';

        const result = await this.sentCashout({
          code: this.bet.code,
          cashout: cashoutValue
        });

        await this.fetchMyBets({ all: false });
        this.showCashoutDialog = false;
        this.$emit('close');

        if (result?.updated) {
          this.$emit('cashout-success');
        } else {
          this.$emit('cashout-error');
        }
      } catch (error) {
        console.error('Cashout error:', error);
        this.$emit('cashout-error');
      } finally {
        this.cashoutLoading = false;
      }
    }
  }
});
